import { mmFetch } from 'api/mmFetch';
import { mmFetchWithResponse } from 'api/mmFetchWithResponse';
import { fileDownload } from 'utils/fileDownload';

const BATCHES_PATH = 'api/warehouse/v1/batches';

/**
 * @param {unknown} value
 * @returns {number | undefined}
 */
function normalizePositiveInteger(value) {
    if (value === undefined || value === null || value === '') {
        return undefined;
    }
    const n = Number(value);
    if (!Number.isInteger(n) || n <= 0) {
        return undefined;
    }
    return n;
}

/**
 * Normalizes a facility id to the positive integer required by `GET /warehouse/v1/batches?facilityID=`.
 *
 * @param {unknown} value
 * @returns {number | undefined}
 */
export function mapFacilityIdToApiParam(value) {
    return normalizePositiveInteger(value);
}

/**
 * Normalizes a batch id to a positive integer path segment for `GET .../batches/{batchID}/pdf`.
 *
 * @param {unknown} value
 * @returns {number | undefined}
 */
export function mapBatchIdToApiPathParam(value) {
    return normalizePositiveInteger(value);
}

/**
 * Builds URLSearchParams for `GET /warehouse/v1/batches`.
 *
 * @param {Object} [params={}]
 * @param {number | string} [params.facilityId] - Facility id, serialized as the API's `facilityID` param
 * @returns {URLSearchParams}
 */
export function buildBatchSearchParams(params = {}) {
    const queryParams = new URLSearchParams();

    const facilityID = mapFacilityIdToApiParam(params.facilityId);
    if (facilityID !== undefined) {
        queryParams.set('facilityID', String(facilityID));
    }

    return queryParams;
}

/**
 * Fetches batches for a facility (`data.rows` from the API envelope).
 *
 * @param {Object} [params={}]
 * @param {number | string} [params.facilityId] - Facility id (required by the backend)
 * @param {Object} [options={}] - Fetch options (e.g. `{ signal }`)
 * @returns {Promise<{status: boolean, msg?: string, data?: {rows?: Array}, statusCode?: number | null}>}
 */
export async function searchBatches(params = {}, options = {}) {
    const facilityID = mapFacilityIdToApiParam(params.facilityId);
    if (facilityID === undefined) {
        return Promise.resolve({
            status: false,
            msg: 'facilityId is required',
            data: {},
            statusCode: null,
        });
    }

    const url = `${BATCHES_PATH}?${buildBatchSearchParams(params).toString()}`;
    return mmFetchWithResponse(url, options);
}

/**
 * Downloads the artifact created when a batch was first printed (`GET .../batches/{batchID}/download`).
 * This is a label PDF for most batches and an orders CSV for pcdata batches.
 *
 * @param {string | number} batchId - Batch primary key
 * @param {Omit<RequestInit, 'body' | 'method'>} [options={}] - Extra fetch options (e.g. `{ signal }`)
 * @returns {Promise<{status: boolean, msg: string, data: Object}>}
 */
export async function downloadBatchFile(batchId, options = {}) {
    const id = mapBatchIdToApiPathParam(batchId);
    if (id === undefined) {
        return {
            status: false,
            msg: 'Invalid batch id',
            data: {},
        };
    }

    const url = `${BATCHES_PATH}/${id}/download`;

    try {
        const res = await mmFetch(url, options);
        if (res.ok) {
            await fileDownload(res, `batch-${id}-print-file`);
            return {
                status: true,
                msg: '',
                data: {},
            };
        }
        const decodedResp = await res.json();
        return {
            status: false,
            msg: decodedResp?.msg ?? 'Failed to download batch file',
            data: decodedResp?.data ?? {},
        };
    } catch (e) {
        return {
            status: false,
            msg: e?.toString?.() ?? 'Failed to download batch file',
            data: {},
        };
    }
}
